import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Send, Loader2, Bot, User, Shield, Calculator, 
  FileText, Building2, Sparkles, CheckCircle2, AlertTriangle,
  ThumbsUp, ThumbsDown, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';

const SPECIALISTS = [
  {
    id: 'zus',
    name: 'Ekspert ZUS',
    icon: Building2,
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
    description: 'Składki, ubezpieczenia społeczne, zasiłki',
    topics: ['Składki ZUS', 'Mały ZUS Plus', 'Zasiłki', 'Ubezpieczenia', 'Emerytury', 'L4'],
    systemPrompt: 'Jesteś ekspertem ds. ZUS - ubezpieczeń społecznych, składek, zasiłków i emerytur w Polsce. Odpowiadaj precyzyjnie z podstawą prawną.'
  },
  {
    id: 'vat',
    name: 'Ekspert VAT',
    icon: Calculator,
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    description: 'Podatek VAT, faktury, odliczenia, KSeF',
    topics: ['Stawki VAT', 'Faktury', 'JPK_VAT', 'KSeF', 'Split Payment', 'Odliczenia'],
    systemPrompt: 'Jesteś ekspertem ds. podatku VAT w Polsce. Znasz ustawę o VAT, interpretacje i orzecznictwo. Odpowiadaj z podstawą prawną.'
  },
  {
    id: 'pit-cit',
    name: 'Ekspert PIT/CIT',
    icon: FileText,
    color: 'from-violet-500 to-purple-600',
    bgColor: 'bg-violet-50 dark:bg-violet-900/20',
    borderColor: 'border-violet-200 dark:border-violet-800',
    description: 'Podatki dochodowe, rozliczenia, ulgi',
    topics: ['PIT', 'CIT', 'Estoński CIT', 'Ulgi podatkowe', 'Amortyzacja', 'KPiR'],
    systemPrompt: 'Jesteś ekspertem ds. podatków dochodowych PIT i CIT w Polsce. Znasz ustawy podatkowe, interpretacje i ulgi. Odpowiadaj z podstawą prawną.'
  }
];

export default function SpecialistBotsPage() {
  const navigate = useNavigate();
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSelectSpecialist = (specialist) => {
    setSelectedSpecialist(specialist);
    setMessages([{
      role: 'assistant',
      content: `Cześć! Jestem ${specialist.name}. Specjalizuję się w: ${specialist.description}. W czym mogę Ci pomóc?`,
      specialist: specialist.id,
      verified: null
    }]);
  };

  const handleSend = async () => {
    if (!input.trim() || loading || !selectedSpecialist) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/specialist/chat`, {
        specialist_id: selectedSpecialist.id,
        message: input,
        system_prompt: selectedSpecialist.systemPrompt
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 60000
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        specialist: selectedSpecialist.id,
        verified: response.data.verification || null,
        sources: response.data.sources || []
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Specialist chat error:', error);
      toast.error('Błąd komunikacji ze specjalistą');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Przepraszam, wystąpił błąd. Spróbuj ponownie.',
        specialist: selectedSpecialist.id,
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (messageIndex) => {
    setVerifying(messageIndex);
    try {
      const token = localStorage.getItem('token');
      const message = messages[messageIndex];
      
      const response = await axios.post(`${BACKEND_URL}/api/specialist/verify`, {
        specialist_id: selectedSpecialist.id,
        response_to_verify: message.content
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 60000
      });

      setMessages(prev => prev.map((msg, idx) => 
        idx === messageIndex ? { ...msg, verified: response.data } : msg
      ));
      toast.success('Weryfikacja zakończona');
    } catch (error) {
      toast.error('Błąd weryfikacji');
    } finally {
      setVerifying(null);
    }
  };

  const getVerificationBadge = (verified) => {
    if (!verified) return null;
    
    if (verified.status === 'verified') {
      return (
        <div className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
          <CheckCircle2 className="w-3 h-3" /> Zweryfikowano
        </div>
      );
    } else if (verified.status === 'warning') {
      return (
        <div className="flex items-center gap-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full">
          <AlertTriangle className="w-3 h-3" /> Wymaga uwagi
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#f8fafb] dark:bg-slate-900">
      {/* Header */}
      <div className="border-b bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              data-testid="specialist-back-btn"
              onClick={() => selectedSpecialist ? setSelectedSpecialist(null) : navigate('/dashboard')} 
              className="gap-2 text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="w-4 h-4" /> {selectedSpecialist ? 'Wybierz specjalistę' : 'Powrót'}
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedSpecialist?.color || 'from-slate-500 to-slate-600'} flex items-center justify-center shadow-lg`}>
                {selectedSpecialist ? <selectedSpecialist.icon className="w-5 h-5 text-white" /> : <Bot className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 dark:text-white">
                  {selectedSpecialist?.name || 'Specjaliści AI'}
                </h1>
                <p className="text-xs text-slate-400">
                  {selectedSpecialist?.description || 'Wybierz eksperta do rozmowy'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 lg:p-8">
        {!selectedSpecialist ? (
          /* Specialist Selection */
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Wybierz specjalistę</h2>
              <p className="text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
                Każdy ekspert ma głęboką wiedzę w swojej dziedzinie i odpowiada z podstawą prawną.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="specialist-cards-grid">
              {SPECIALISTS.map((specialist) => (
                <div
                  key={specialist.id}
                  data-testid={`specialist-card-${specialist.id}`}
                  onClick={() => handleSelectSpecialist(specialist)}
                  className={`group p-6 bg-white dark:bg-slate-800 rounded-2xl border-2 ${specialist.borderColor} hover:shadow-xl hover:scale-[1.02] cursor-pointer transition-all duration-300`}
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${specialist.color} flex items-center justify-center text-white mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <specialist.icon className="w-8 h-8" />
                  </div>
                  
                  <h3 className="font-bold text-xl text-slate-800 dark:text-white mb-2">{specialist.name}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{specialist.description}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {specialist.topics.slice(0, 4).map((topic, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded-full ${specialist.bgColor} text-slate-600 dark:text-slate-300`}>
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Features info */}
            <div className="mt-8 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800">
              <h4 className="font-semibold text-slate-800 dark:text-white mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" /> Co wyróżnia naszych specjalistów?
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                  <span className="text-slate-600 dark:text-slate-300">Odpowiedzi z podstawą prawną (artykuły, ustawy)</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                  <span className="text-slate-600 dark:text-slate-300">Weryfikacja odpowiedzi przez drugi model AI</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                  <span className="text-slate-600 dark:text-slate-300">Aktualna wiedza o przepisach i interpretacjach</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Chat Interface */
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar - Topics */}
            <div className="lg:col-span-1">
              <div className={`p-4 rounded-2xl ${selectedSpecialist.bgColor} border ${selectedSpecialist.borderColor}`}>
                <h3 className="font-semibold text-slate-800 dark:text-white mb-3">Popularne tematy</h3>
                <div className="space-y-2">
                  {selectedSpecialist.topics.map((topic, idx) => (
                    <Button
                      key={idx}
                      variant="ghost"
                      size="sm"
                      onClick={() => setInput(`Wyjaśnij: ${topic}`)}
                      className="w-full justify-start text-xs hover:bg-white/50"
                    >
                      {topic}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Chat Area */}
            <div className="lg:col-span-3 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col h-[600px]">
              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((message, idx) => (
                    <div key={idx} className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.role === 'user' 
                          ? 'bg-slate-200 dark:bg-slate-600' 
                          : `bg-gradient-to-br ${selectedSpecialist.color}`
                      }`}>
                        {message.role === 'user' ? (
                          <User className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        ) : (
                          <selectedSpecialist.icon className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className={`max-w-[80%] ${message.role === 'user' ? 'text-right' : ''}`}>
                        <div className={`p-4 rounded-2xl ${
                          message.role === 'user'
                            ? 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-white'
                            : message.error 
                              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                              : `${selectedSpecialist.bgColor} text-slate-700 dark:text-slate-200`
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                        
                        {/* Sources from knowledge base */}
                        {message.sources && message.sources.length > 0 && (
                          <div className="mt-2 px-3 py-2 bg-white/60 dark:bg-slate-700/40 rounded-xl border border-slate-200/50 dark:border-slate-600/30">
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Źródła:</p>
                            <div className="flex flex-wrap gap-1">
                              {message.sources.map((src, sIdx) => (
                                <a 
                                  key={sIdx}
                                  href={src.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  {src.title || src.portal || 'Źródło'}
                                  {sIdx < message.sources.length - 1 ? ',' : ''}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Verification section for assistant messages */}
                        {message.role === 'assistant' && !message.error && (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {getVerificationBadge(message.verified)}
                            
                            {!message.verified && (
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`verify-btn-${idx}`}
                                onClick={() => handleVerify(idx)}
                                disabled={verifying === idx}
                                className="text-xs text-slate-500 hover:text-slate-700"
                              >
                                {verifying === idx ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Weryfikuję...</>
                                ) : (
                                  <><RefreshCw className="w-3 h-3 mr-1" /> Zweryfikuj odpowiedź</>
                                )}
                              </Button>
                            )}
                            
                            {message.verified?.details && (
                              <p className="text-xs text-slate-500 w-full mt-1" data-testid={`verify-details-${idx}`}>{message.verified.details}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${selectedSpecialist.color} flex items-center justify-center`}>
                        <selectedSpecialist.icon className="w-4 h-4 text-white" />
                      </div>
                      <div className={`p-4 rounded-2xl ${selectedSpecialist.bgColor}`}>
                        <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Input */}
              <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    data-testid="specialist-chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    placeholder={`Zadaj pytanie ${selectedSpecialist.name}...`}
                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    disabled={loading}
                  />
                  <Button
                    data-testid="specialist-chat-send-btn"
                    onClick={handleSend}
                    disabled={loading || !input.trim()}
                    className={`bg-gradient-to-r ${selectedSpecialist.color} hover:opacity-90 text-white px-6`}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
